import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Billboard, Sphere } from '@react-three/drei'
import { FakeGlowMaterial } from './GlowMaterial'

export interface PointOfInterest {
  id: number
  name: string
  infoPanel: {
    id: number
    title: string
    infoTab1: {
      tabName: string
      title: string
      bodyColumn1: string
      bodyColumn2: string
      audio: {
        id: number
        alt: string
        fileType: string
        updatedAt: string
        createdAt: string
        url: string
        thumbnailURL: string | null
        filename: string
        mimeType: string
        filesize: number
        width: number
        height: number
        focalX: number
        focalY: number
      } | null
    }
    infoTab2: {
      tabName: string
      title: string
      bodyColumn1: string
      bodyColumn2: string
      audio: {
        id: number
        alt: string
        fileType: string
        updatedAt: string
        createdAt: string
        url: string
        thumbnailURL: string | null
        filename: string
        mimeType: string
        filesize: number
        width: number
        height: number
        focalX: number
        focalY: number
      } | null
    }
    infoTab3: {
      tabName: string
      title: string
      bodyColumn1: string
      bodyColumn2: string
      audio: {
        id: number
        alt: string
        fileType: string
        updatedAt: string
        createdAt: string
        url: string
        thumbnailURL: string | null
        filename: string
        mimeType: string
        filesize: number
        width: number
        height: number
        focalX: number
        focalY: number
      } | null
    }
    infoTab4: {
      tabName: string
      title: string
      bodyColumn1: string
      bodyColumn2: string
      audio: {
        id: number
        alt: string
        fileType: string
        createdAt: string
        updatedAt: string
        url: string
        thumbnailURL: string | null
        filename: string
        mimeType: string
        filesize: number
        width: number
        height: number
        focalX: number
        focalY: number
      } | null
    }
    updatedAt: string
    createdAt: string
  }
  mode: string
  position: {
    x: number
    y: number
    z: number
  }
  cameraPosition: {
    x: number
    y: number
    z: number
    targetX: number
    targetY: number
    targetZ: number
    azimuthRotation?: number
    polarRotation?: number
  }
  model: {
    id: number
    title: string
    source: {
      id: number
      alt: string
      fileType: string
      updatedAt: string
      createdAt: string
      url: string
      thumbnailURL: string | null
      filename: string
      mimeType: string
      filesize: number
      width: number | null
      height: number | null
      focalX: number | null
      focalY: number | null
    }
    cameraDriverMode: {
      x: number
      y: number
      z: number
      targetX: number
      targetY: number
      targetZ: number
      azimuthRotation?: number
      polarRotation?: number
      maxPolarAngle: number
      minPolarAngle: number
      maxAzimuthAngle: number
      minAzimuthAngle: number
    }
    cameraEngineerMode: {
      x: number
      y: number
      z: number
      targetX: number
      targetY: number
      targetZ: number
      azimuthRotation?: number
      polarRotation?: number
      maxPolarAngle: number
      minPolarAngle: number | null
      maxAzimuthAngle: number | null
      minAzimuthAngle: number | null
    }
    pointOfInterests: {
      docs: number[]
      hasNextPage: boolean
    }
    updatedAt: string
    createdAt: string
  }
  views: number
  updatedAt: string
  createdAt: string
}

// Point of Interest Light Component
export const PointOfInterestLight = ({
  poi,
  isActive,
  isVisited,
  primaryColour,
  secondaryColour,
  mode,
  onClick,
}: {
  poi: PointOfInterest
  isActive: boolean
  isVisited: boolean
  primaryColour: string
  secondaryColour: string
  mode: string
  onClick: () => void
}) => {
  const meshLightRef = useRef<THREE.Mesh>(null)
  const meshRef = useRef<THREE.Mesh>(null)
  const pulseDirection = useRef(1)

  useFrame((_, delta) => {
    if (!meshLightRef.current?.material) return
    const material = meshLightRef.current.material as unknown as {
      glowColor: { setHex: (color: number) => void }
      opacity: number
      glowInternalRadius: number
    }

    if (isActive) {
      material.glowColor.setHex(
        typeof secondaryColour === 'string'
          ? parseInt(secondaryColour.replace(/^#/, ''), 16)
          : secondaryColour || 0xbe95ff
      )
      material.opacity = 0.3
      material.glowInternalRadius = 0.1
    } else if (isVisited) {
      material.glowColor.setHex(
        typeof secondaryColour === 'string'
          ? parseInt(secondaryColour.replace(/^#/, ''), 16)
          : secondaryColour || 0xe8daff
      )
      material.opacity = 0.3
      material.glowInternalRadius = 0.1
    } else {
      // Blue pulsing light
      material.glowColor.setHex(
        typeof secondaryColour === 'string'
          ? parseInt(secondaryColour.replace(/^#/, ''), 16)
          : secondaryColour || 0xe8daff
      )

      // Pulsing animation
      material.opacity += delta * 1 * pulseDirection.current
      if (material.opacity >= 0.5) {
        material.opacity = 0.5
        pulseDirection.current = -1
      } else if (material.opacity <= 0.1) {
        material.opacity = 0.1
        pulseDirection.current = 1
      }
      material.glowInternalRadius = 0.1 + material.opacity * 0.3 // Animate between 0.3 and 0.9
    }
  })

  const scaleFactor = useMemo(() => {
    if (mode === 'engineer') {
      return 8
    }
    return 1
  }, [mode])

  return (
    <group position={[poi.position.x, poi.position.y, poi.position.z]}>
      <Sphere
        ref={meshLightRef}
        args={[0.01 * scaleFactor, 100, 100]}
        onClick={onClick}
        renderOrder={100}
      >
        <FakeGlowMaterial glowColor={secondaryColour} opacity={0.3} glowInternalRadius={1} />
      </Sphere>
      <Sphere
        ref={meshRef}
        args={[0.008 * scaleFactor, 100, 100]}
        renderOrder={101}
      >
        <meshBasicMaterial
          color={primaryColour}
          transparent={true}
          opacity={1}
          depthTest={false}
          depthWrite={false}
          toneMapped={false}
        />
      </Sphere>

      {/* Billboarded '+' symbol */}
      <Billboard>
        <group position={[0, 0, 0]}>
          {/* Vertical line of the plus */}
          <mesh renderOrder={200}>
            <planeGeometry args={[0.0006 * scaleFactor, 0.005 * scaleFactor]} />
            <meshBasicMaterial
              color={secondaryColour}
              transparent={true}
              opacity={1}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
          {/* Horizontal line of the plus */}
          <mesh renderOrder={200}>
            <planeGeometry args={[0.005 * scaleFactor, 0.0006 * scaleFactor]} />
            <meshBasicMaterial
              color={secondaryColour}
              transparent={true}
              opacity={1}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
        </group>
      </Billboard>
    </group>
  )
}
